import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import shortid from 'shortid'
import { distance } from 'fastest-levenshtein'

import styles from '../component-styles/select-box.module.scss'
import stylesAlt from '../component-styles/select-box-alt.module.scss'
import Arrow from '../svgs/arrow'
import Input from './input'
import Button from './button'
import { TagsWrapper, Tag } from './add-multi-values-tags'
import { saveToCopy, setCurrentDropDown } from '../store/actions'
import { getItemByKeyValue } from '../helpers'
import { UiState } from '../store/ui-reducer'
import { Preloader } from './loader'

interface Props {
  children?: React.ReactNode
  value?: string | React.ReactNode
  className?: string
  style?: any
  type?: 'email'
  icon?: React.ReactNode
  placeholder?: string
  disabled?: boolean
}

export default function SelectBox({
  children,
  value,
  style,
  type,
  className,
  icon,
  disabled,
}: Props): React.ReactElement {
  const [active, setActive] = useState(false)
  const ref: any = useRef(null)

  const handleAction = (): void => {
    if (!disabled) {
      setActive((activeState: boolean): boolean => !activeState)
    }
  }

  return (
    <div
      ref={ref}
      className={`${active ? styles.containerActive : styles.container} ${
        disabled ? styles.disabled : ''
      } ${className}`}
      onClick={handleAction}
      // onKeyDown={handleAction}
      onBlur={(e: any): void => {
        if (ref && ref.current && !ref.current.contains(e.relatedTarget)) {
          setActive(false)
        }
      }}
      style={style}
      role="button"
      tabIndex={-1}
      aria-hidden="true"
    >
      <h4 className={type === 'email' ? styles.typeEmail : ''}>{value}</h4>
      {icon || <Arrow fill="#D4366A" />}
      <div className={active ? styles.expanded : styles.box}>{children}</div>
    </div>
  )
}

interface PropsSelectBoxButton {
  children?: React.ReactNode
  value?: string | React.ReactNode
  className?: string
  disabled?: boolean
  action?: boolean
  onClick?: (boolean) => void
}

export function SelectBoxButton({
  children,
  value,
  className,
  disabled,
  action,
  onClick,
}: PropsSelectBoxButton): React.ReactElement {
  const [active, setActive] = useState(false)
  const ref: any = useRef(null)

  const handleAction = (): void => {
    if (!disabled) {
      const newActive = !active
      setActive(newActive)
      if (onClick) {
        onClick(newActive)
      }
    }
  }

  return (
    <div
      ref={ref}
      className={classNames(className, stylesAlt.container, {
        [stylesAlt.containerActive]: active,
        [stylesAlt.disabled]: disabled,
        [stylesAlt.action]: action,
      })}
      onClick={handleAction}
      onBlur={(e: any): void => {
        if (ref && ref.current && !ref.current.contains(e.relatedTarget)) {
          setActive(false)
        }
      }}
      role="button"
      tabIndex={-1}
      aria-hidden="true"
    >
      <p className={stylesAlt.value}>{value}</p>
      <Button className={stylesAlt.expandButton} type="none">
        {action ? <Arrow fill="#ffffff" /> : <Arrow fill="#e61969" />}
      </Button>
      <div
        className={classNames(stylesAlt.box, {
          [stylesAlt.expanded]: active,
        })}
      >
        {children}
      </div>
    </div>
  )
}

interface SimpleProps {
  children?: React.ReactNode
  value?: string | number
  onChange?: any
  className?: string
  label?: string
  disabled?: boolean
  name: string
}

export function SelectBoxSimple({
  disabled,
  children,
  value,
  onChange,
  className = '',
  label = '',
  name,
}: SimpleProps): React.ReactElement {
  const inner = () => (
    <span className={styles.innerWrapper}>
      <select
        disabled={disabled}
        id={name}
        name={name}
        className={styles.selector}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const { value: val } = e.target as HTMLSelectElement
          onChange(val)
        }}
      >
        {children}
      </select>
    </span>
  )

  if (!label) {
    return (
      <span className={`${styles.selectorWrapper} ${className}`}>
        {inner()}
      </span>
    )
  }

  return (
    <label className={`${styles.selectorWrapper} ${className}`} htmlFor={name}>
      <span className={styles.label}>{label}</span>
      {inner()}
    </label>
  )
}

export const getSummary = (vals, items, keyName) => {
  let res = ''
  if (vals.length > 0 && vals.length < items.length) {
    const half = Math.floor(items.length / 2)
    const notSelected = items
      .map((i) => {
        const found = getItemByKeyValue(vals, keyName, i[keyName])
        return found !== -1 ? -1 : i[keyName]
      })
      .filter((i) => i !== -1)
    // console.log(notSelected)
    if (vals.length > half) {
      // @ts-ignore
      res = `Exclude ${notSelected.join(', ')}`
    } else {
      const names = vals.map((i) => i[keyName])
      res = `${names.join(', ')}`
    }
  }
  return res
}

interface MultiSelectBoxProps {
  id: string
  value: string[]
  className?: string
  items: any[]
  keyName?: string
  keyValue?: string
  onChange: (value: string[], parentID?: string) => void
  showTags?: boolean
  typeAhead?: boolean
  showNoneWithTypeAhead?: boolean
  showAllWithTypeAhead?: boolean
  allValue?: string
  placeholder?: string
  valsToCopy?: string[]
  emptyTagsPlaceholder?: string
  labels?: {
    items: string[]
    title: string
    icon?: string
    parentID?: string
  }[]
  loading?: boolean
  showLabelsWithTags?: boolean
  inline?: boolean
  inlineLabel?: string
  showOnlyButton?: boolean
  disabled?: boolean
  defaultError?: boolean
}

export function MultiSelectBox({
  id,
  value,
  className,
  items,
  keyName = 'optionName',
  keyValue = 'optionValue',
  onChange,
  showTags = false,
  typeAhead = false,
  showNoneWithTypeAhead = false,
  showAllWithTypeAhead = false,
  allValue = 'All',
  placeholder,
  emptyTagsPlaceholder = '',
  valsToCopy,
  labels = [],
  showLabelsWithTags = true,
  loading = false,
  inline = false,
  inlineLabel = '',
  showOnlyButton = false,
  disabled = false,
  defaultError = false,
}: MultiSelectBoxProps): React.ReactElement {
  const [active, setActive] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const { currentDropDown } = useSelector(({ ui }: { ui: UiState }) => ui)

  const ref: any = useRef(null)
  const dispatch = useDispatch()

  const handleAction = (e: React.MouseEvent): void => {
    const activeState = !active
    dispatch(setCurrentDropDown({ id: activeState ? id : '' }))
    if (!activeState) {
      setSearchTerm('')
    }
    setActive(activeState)
  }

  const box = classNames(styles.multiSelectBox, {
    [styles.multiSelectExpanded]: active,
  })

  const vals = useMemo(() => {
    return items
      .map((s) => {
        if (value.indexOf(s[keyValue]) !== -1) {
          return s
        }
        return null
      })
      .filter((a) => a !== null)
  }, [value])

  const hintText = useMemo(() => {
    return getSummary(vals, items, keyName)
  }, [vals, items, keyName])

  const title = useMemo(() => {
    if (inline && inlineLabel !== '') {
      const res = getSummary(vals, items, keyName)
      return `${inlineLabel}${res !== '' ? `: ${res}` : ''}`
    }

    if (!typeAhead && vals.length > 0) {
      if (vals.length === 1) {
        // @ts-ignore
        return `${vals[0][keyName]}`
      }
      if (items.length === vals.length) {
        return allValue
      }
      // @ts-ignore
      return `${vals[0][keyName]} +${vals.length - 1}`
    }
    return placeholder || 'None'
  }, [vals, typeAhead, placeholder])

  useEffect(() => {
    if (currentDropDown !== '') {
      if (active && currentDropDown !== id) {
        setSearchTerm('')
        setActive(false)
      }
    }
  }, [currentDropDown])

  const updateValue = (item: any, checked: boolean, parentId?: string) => {
    if (checked) {
      // This needs to happen to keep the items in order
      const key = [...value, item[keyValue]]
      const changedValue = items
        .map((s) => {
          if (key.indexOf(s[keyValue]) !== -1) {
            return s[keyValue]
          }
          return null
        })
        .filter((a) => a !== null)
      // console.log(parentId, key, items, changedValue)
      // .map(i => `${i}\n${parentId}`)
      onChange(changedValue, parentId)
    } else {
      onChange(
        value.filter((val) => val !== item[keyValue]),
        parentId,
      )
    }
  }

  const itemsFiltered = useMemo(() => {
    if (!typeAhead) {
      return items
    }
    const result = items.filter((item: string | any): boolean => {
      const toSearch = typeof item === 'object' ? item[keyName] : item
      if (toSearch && searchTerm !== '') {
        const pattern = searchTerm
          .replace(/[^a-zA-Z0-9 ]/g, '')
          .split('')
          .join('.*')
        return toSearch.toLowerCase().match(new RegExp(pattern, 'i')) !== null
      }
      return searchTerm === ''
    })

    if (searchTerm !== '') {
      result.sort((a, b) => {
        const distA = distance(
          searchTerm,
          typeof a === 'object' ? a[keyName] : a,
        )
        const distB = distance(
          searchTerm,
          typeof b === 'object' ? b[keyName] : b,
        )
        return distA - distB
      })
    }
    return result
  }, [items, searchTerm, typeAhead, keyName])
  const renderItem = (item, idPrefix = ''): React.ReactElement => {
    return (
      <Input
        key={item[keyValue]}
        type="checkbox"
        id={`${idPrefix}${id}${item[keyValue]}`}
        name={`${idPrefix}${id}${item[keyValue]}`}
        label={item[keyName]}
        checked={value.indexOf(item[keyValue]) !== -1}
        className={styles.selectItem}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          const { checked } = e.target as HTMLInputElement
          updateValue(item, checked, idPrefix)
        }}
      >
        {showOnlyButton && (
          <Button
            onClick={() => {
              // updateValue(item, true, idPrefix)
              onChange([item[keyValue]], idPrefix)
            }}
            type="flatGrey"
            className={styles.onlyButton}
          >
            Only
          </Button>
        )}
      </Input>
    )
  }

  const renderItems = () => {
    if (labels.length > 0) {
      const result = labels.map((label) => {
        const sorted = label.items
          .map((optionID) => {
            return getItemByKeyValue(itemsFiltered, 'optionID', optionID)
          })
          .filter((i) => i !== -1)

        if (sorted.length > 0) {
          return (
            <span key={shortid.generate()}>
              <span className={styles.itemLabel}>
                <span>
                  <>{label.title}</>
                </span>
              </span>
              {sorted.map((item) => renderItem(item, label.parentID))}
            </span>
          )
        }
        return null
      })
      return result
    }

    return itemsFiltered.map((item) => {
      if (showTags && value.indexOf(item[keyValue]) !== -1) {
        return null
      }
      return renderItem(item)
    })
  }

  const renderTag = (tag) => {
    return (
      <Tag
        key={shortid.generate()}
        onClick={() => {
          updateValue(tag, false)
        }}
      >
        {tag[keyName]}
      </Tag>
    )
  }

  const renderTags = () => {
    if (labels.length > 0 && showLabelsWithTags) {
      return (
        <TagsWrapper className={styles.tagsWrapper}>
          <>
            {labels.map((label) => {
              const sorted = label.items
                .map((optionID) => {
                  return getItemByKeyValue(itemsFiltered, 'optionID', optionID)
                })
                .filter((i) => i !== -1)

              if (sorted.length > 0) {
                const valsToRender = vals
                  .map((tag: any) => {
                    if (tag[keyName] === '') {
                      return null
                    }
                    const { optionID } = tag
                    if (label.items.indexOf(optionID) === -1) {
                      return null
                    }
                    return tag
                  })
                  .filter((tag) => tag !== null)
                if (valsToRender.length === 0) {
                  return null
                }
                return (
                  <span
                    key={shortid.generate()}
                    className={styles.tagsLabelWrapper}
                  >
                    <span className={styles.itemLabel}>
                      <span>
                        <>{label.title}</>
                      </span>
                    </span>
                    <span className={styles.tagsLabelBody}>
                      {valsToRender.map((tag: any) => {
                        return (
                          <span key={shortid.generate()}>{renderTag(tag)}</span>
                        )
                      })}
                    </span>
                  </span>
                )
              }
              return null
            })}
          </>
        </TagsWrapper>
      )
    }

    return (
      <TagsWrapper className={styles.tagsWrapper}>
        <>
          {vals.map((tag: any) => {
            if (tag[keyName] === '') {
              return null
            }
            return <span key={shortid.generate()}>{renderTag(tag)}</span>
          })}
        </>
      </TagsWrapper>
    )

    // return itemsFiltered.map((item) => {
    //   if (showTags && value.indexOf(item[keyValue]) !== -1) {
    //     return null
    //   }
    //   return renderItem(item)
    // })
  }

  const containerClassNames = classNames(
    className,
    styles.multiSelectContainer,
    {
      [styles.active]: active,
      [styles.showTags]: showTags,
      [styles.disabled]: disabled,
      [styles.multiSelectContainerError]: defaultError,
    },
  )

  return (
    <div ref={ref} className={containerClassNames}>
      <span
        onClick={handleAction}
        onDoubleClick={(e: any) => {
          if (vals.length > 0) {
            // copy into
            dispatch(saveToCopy(vals))
          } else if (vals.length === 0 && valsToCopy && valsToCopy.length > 0) {
            // eslint-disable-next-line
            // debugger
            onChange(valsToCopy.map((item) => item[keyValue]))
          }
        }}
        className={styles.innerInputWrapper}
        role="button"
        aria-hidden="true"
        tabIndex={-1}
      >
        {showTags && vals.length > 0 && <>{renderTags()}</>}
        {showTags && vals.length === 0 && emptyTagsPlaceholder !== '' && (
          <p className={styles.tagValue}>{emptyTagsPlaceholder}</p>
        )}
        {loading && (
          <div className={styles.preloader}>
            <Preloader style={{ width: 25, height: 18, margin: 0 }} />
            <span>Loading...</span>
          </div>
        )}
        {!loading && inline && active && (
          <Input
            type="checkbox"
            id={`${id}-toggle`}
            name={`${id}-toggle`}
            label={inlineLabel}
            checked={value.length === itemsFiltered.length}
            className={classNames(styles.titleSelectItem, {
              [styles.betweenState]:
                value.length > 0 && value.length !== itemsFiltered.length,
            })}
            onClick={(e) => {
              if (value.length === itemsFiltered.length) {
                onChange([])
              } else {
                onChange(itemsFiltered.map((item) => item[keyValue]))
              }
              e.preventDefault()
              e.stopPropagation()
            }}
          />
        )}
        {!loading && !(inline && active) && !showTags && (
          <p className={styles.value}>{title}</p>
        )}

        <Button className={styles.expandButton} type="none" />
      </span>
      {!active && inline && hintText !== '' && (
        <span className={styles.hint}>{hintText}</span>
      )}
      {active && (
        <div className={box}>
          {typeAhead && (
            <Input
              id={`${id}-input`}
              name={`${id}-input`}
              autoComplete="off"
              showClear
              placeholder={placeholder}
              className={classNames(styles.input)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                const {
                  target: { value: sVal },
                } = e
                setSearchTerm(sVal)
              }}
            />
          )}
          <span
            key={`${JSON.stringify(itemsFiltered)}`}
            className={styles.dropDownWrapper}
          >
            {(!typeAhead ||
              (typeAhead && showAllWithTypeAhead && searchTerm === '')) && (
              <Input
                type="checkbox"
                id={`${id}-all`}
                name={`${id}-all`}
                label={allValue}
                checked={value.length === itemsFiltered.length}
                className={classNames(styles.selectItem, {
                  [styles.selectItemDivider]:
                    showAllWithTypeAhead && !showNoneWithTypeAhead,
                })}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  const { checked } = e.target as HTMLInputElement
                  if (!checked) {
                    onChange([])
                  } else {
                    onChange(itemsFiltered.map((item) => item[keyValue]))
                  }
                }}
              />
            )}
            {(!typeAhead ||
              (typeAhead && showNoneWithTypeAhead && searchTerm === '')) && (
              <Input
                type="checkbox"
                id={`${id}-none`}
                name={`${id}-none`}
                label="None"
                checked={value.length === 0}
                className={classNames(
                  styles.selectItem,
                  styles.selectItemDivider,
                )}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  const { checked } = e.target as HTMLInputElement
                  if (checked) {
                    onChange([])
                  } else {
                    onChange(itemsFiltered.map((item) => item[keyValue]))
                  }
                }}
              />
            )}
            {renderItems()}
          </span>
        </div>
      )}
    </div>
  )
}
